import { useEffect, useState } from "react";

import {
    GetSinglePropertyDriversAndWorksheetItemsNoCalcEngineQuery,
    PendingUpdates,
    useGetSinglePropertyDriversAndWorksheetItemsNoCalcEngineLazyQuery,
    useSetDriversMutation,
    VersionType
} from "../../../__generated__/generated_types";

import { hydrateDriverAssumptionsNoCalcEngine, TDriverMetrics } from "./logic/driversAndWorksheetData";


interface ISaveData {
    isUserRequestingWorksheetDeletion: boolean,
    pendingUpdates: PendingUpdates,
    accountId: string,
    propertyId: string,
    year: number,
    versionType: VersionType,
}

export interface IDriversAndWorksheetDataNoCalcEngine {
    isReady: boolean,

    setPropertyId: (propertyId: string) => void,
    propertyId: string|undefined,

    setAccountId: (accountId: string) => void,
    accountId: string|undefined,

    fetch: (year: number, versionType:VersionType) => void,

    saveData: (args: ISaveData) => void,

    rawData: GetSinglePropertyDriversAndWorksheetItemsNoCalcEngineQuery|undefined,
    parsedData: TDriverMetrics|undefined,
    dataLoading: boolean,
    resetData: () => void,
}

export interface IAccountDriversAndWorksheetDataParams {
    year: number|undefined,
    versionType: VersionType|undefined,
    currentPropertyId: string|undefined,
    accountId: string,
}

interface IUseDriversAndWorksheetDataProps {
    propertyId: string|undefined,
    accountId: string|undefined,
    budgetYear: number|undefined
}

export default function useDriversAndWorksheetData(props: IUseDriversAndWorksheetDataProps): IDriversAndWorksheetDataNoCalcEngine {
    const [isReady, setIsReady] = useState<boolean>(false);

    const [propertyId, _setPropertyId] = useState<string|undefined>(props.propertyId);
    const [accountId, _setAccountId] = useState<string|undefined>(props.accountId);

    const [SetDrivers, {
        data,
        loading: setDriversLoading,
    }] = useSetDriversMutation({
        notifyOnNetworkStatusChange: true
    });

    /**
     * isReady will be set to true when all dependencies checked here are available.
     */
    useEffect(
        () => {
            if(
                accountId == undefined
                || propertyId == undefined
            ){
                return;
            }
            setIsReady(true);
        },
        [propertyId, accountId]
    );

    useEffect(
        () => {
            if(!props.accountId){
                resetData();
                return;
            }
            setAccountId(props.accountId);
        },
        [props.accountId]
    );

    useEffect(
        () => {
            if(!props.propertyId){
                resetData();
                return;
            }
            setAccountId(props.propertyId);
        },
        [props.propertyId]
    );

    // Drivers and Worksheet Line Items data
    const [_fetchDriverAndWorksheetData, {
        data: rawData,
        loading: dataLoading,
        // error: dataError
    }] = useGetSinglePropertyDriversAndWorksheetItemsNoCalcEngineLazyQuery({
        fetchPolicy: "no-cache",
    });

    const setPropertyId = (propertyId: string): void => {
        _setPropertyId(propertyId);
    };

    const setAccountId = (accountId: string): void => {
        _setAccountId(accountId);
    };

    const fetch = (year: number, versionType:VersionType): void => {
        if(
            accountId === undefined
            || propertyId === undefined
            || !isReady
            || year === undefined
            || versionType === undefined
            || props.budgetYear === undefined
        ){
            return;
        }

        _fetchDriverAndWorksheetData({
            variables: {
                propertyId,
                accountId,
                year,
                versionType,
                budgetYear: props.budgetYear
            }
        });
    };

    const resetData = (): void => {
        setParsedData(undefined);
    };

    /**
     * Re-fetch drivers after SetDrivers() executes successfully
     */
    useEffect(() => {
        if (data === undefined || data === null) {
            return;
        }

        fetch(data.setDrivers.year, data.setDrivers.versionType);
    }, [data]);

    const saveData = (args: ISaveData): void => {
        SetDrivers({
            variables: {
                accountId: args.accountId,
                propertyId: args.propertyId,
                year: args.year,
                versionType: args.versionType,
                isUserRequestingWorksheetDeletion: args.isUserRequestingWorksheetDeletion,
                pendingUpdates: args.pendingUpdates,
            },
        });
    };

    const [parsedData, setParsedData] = useState<TDriverMetrics>();
    useEffect(
        () => {
            if(dataLoading || rawData === undefined){
                return;
            }

            setParsedData( prevState => {
                return {
                    ...prevState ?? {},
                    ...hydrateDriverAssumptionsNoCalcEngine(rawData),
                };
            });

        },
        [dataLoading, rawData]
    );

    return {
        isReady,

        setPropertyId,
        propertyId,

        setAccountId,
        accountId,

        fetch,

        resetData,

        saveData,

        rawData,
        parsedData,
        dataLoading: dataLoading || setDriversLoading,
    };
}
